export function calculatePriceExtremum<X>(
  data: X[],
  priceField: keyof X,
  type: "min" | "max",
): number {
  if (!data.length) return 0; // Handle empty array case

  return (
    data.reduce(
      (acc, curr) => {
        const value = curr[priceField];
        const num = typeof value === "number" ? value : Number(value);
        // Skip invalid numbers (NaN, etc.)
        if (Number.isNaN(num)) return acc;
        // Initialize acc with first valid number
        return acc === null ? num : Math[type](acc, num);
      },
      null as number | null,
    ) ?? 0
  );
}

export function calculateTimeExtremum<X>(
  data: X[],
  timeField: keyof X,
  type: "min" | "max",
): string {
  if (!data.length) return "00:00";

  return data.reduce(
    (acc, curr) => {
      const currentTime = curr[timeField] as string;
      if (!/^\d{2}:\d{2}$/.test(currentTime)) return acc;

      const [currHours, currMinutes] = currentTime.split(":").map(Number);
      const [accHours, accMinutes] = acc.split(":").map(Number);

      const currTotalMinutes = currHours * 60 + currMinutes;
      const accTotalMinutes = accHours * 60 + accMinutes;

      if (type === "max") {
        return currTotalMinutes > accTotalMinutes ? currentTime : acc;
      }
      return currTotalMinutes < accTotalMinutes ? currentTime : acc;
    },
    type === "max" ? "00:00" : "23:59",
  );
}

export function createNewArray<T>(
  length: number,
  initialValue: T,
  fn?: (value: T, index: number) => T,
): T[] {
  return Array.from({ length }, fn ? fn : () => initialValue);
}

export function createUniqueArrayItems<T>(
  data: T[],
): (T extends object ? T & { id: string } : T)[] {
  return data
    .filter((item): item is NonNullable<T> => item != null)
    .map((item, index) => {
      if (typeof item === "object" && item !== null && !Array.isArray(item)) {
        return { ...item, id: `id-${index}` } as T extends object
          ? T & { id: string }
          : T;
      }
      return item as T extends object ? T & { id: string } : T;
    });
}

export function generateNameLabelOptions<T>(
  items: T[],
  valueKey: keyof T,
  labelKey: keyof T,
): { name: string; label: string }[] {
  const uniqueValues = [...new Set(items.map((item) => item[valueKey]))];

  return uniqueValues.map((value) => ({
    name: String(value),
    label: String(
      items.find((item) => item[valueKey] === value)?.[labelKey] || value,
    ),
  }));
}
